/**
 * Act1BottomPanel — 第一幕底部面板
 *
 * 手牌区域 + 静观其变 + 目标选择
 */
import React, { useState } from 'react';
import { Eye, Target } from 'lucide-react';
import type { Act1Card } from '@shared/types/cards';
import type { Character } from '@shared/types/character';
import type { FamilyMember } from '@shared/types/family';
import { ACT1_HAND_SIZE } from '@shared/constants/gameBalance';

interface Act1BottomPanelProps {
  hand: Act1Card[];
  deckSize: number;
  phase: string;
  protagonistA: Character;
  protagonistB: Character;
  familyA: FamilyMember[];
  familyB: FamilyMember[];
  onPlayCard: (card: Act1Card, targetId: string) => void;
  onSkip: () => void;
}

export const Act1BottomPanel: React.FC<Act1BottomPanelProps> = React.memo(({
  hand,
  deckSize,
  phase,
  protagonistA,
  protagonistB,
  familyA,
  familyB,
  onPlayCard,
  onSkip,
}) => {
  const isPlayerTurn = phase === 'PLAYER_TURN';
  const [selectedCard, setSelectedCard] = useState<Act1Card | null>(null);

  const allTargets = [
    { id: protagonistA.id, name: protagonistA.name, role: '男方' },
    { id: protagonistB.id, name: protagonistB.name, role: '女方' },
    ...familyA.map(f => ({ id: f.id, name: f.name, role: `男方${f.role}` })),
    ...familyB.map(f => ({ id: f.id, name: f.name, role: `女方${f.role}` })),
  ];

  const handleCardClick = (card: Act1Card) => {
    if (!isPlayerTurn) return;
    setSelectedCard(card);
  };

  const handleTargetSelect = (targetId: string) => {
    if (!selectedCard) return;
    onPlayCard(selectedCard, targetId);
    setSelectedCard(null);
  };

  return (
    <div className="bg-white/95 backdrop-blur-md border-t border-[#E5E0D8] p-3 shrink-0 safe-area-bottom shadow-lg">
      {/* Target selection overlay */}
      {selectedCard && (
        <div className="mb-3 p-2 bg-[#F5EDE3] rounded-xl">
          <div className="flex items-center gap-1 mb-2">
            <Target size={12} className="text-coral-500" />
            <span className="text-[10px] font-medium text-[#6B5B4F]">
              选择「{selectedCard.name}」的目标：
            </span>
            <button
              onClick={() => setSelectedCard(null)}
              className="ml-auto text-[10px] text-[#9CA3AF] hover:text-[#6B7280]"
            >
              取消
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {allTargets.map(t => (
              <button
                key={t.id}
                onClick={() => handleTargetSelect(t.id)}
                className="px-2.5 py-1 text-[10px] bg-white rounded-full border border-[#E5E0D8] hover:border-coral-300 hover:bg-coral-50 transition-colors"
              >
                {t.name}（{t.role}）
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Deck info */}
      <div className="flex justify-end items-center mb-2 px-1">
        <div className="flex items-center gap-2 text-[10px] text-[#6B7280]">
          <span className="px-2 py-0.5 bg-[#F5EDE3] rounded-full font-medium">
            牌堆 {deckSize}
          </span>
          <span className="px-2 py-0.5 bg-[#F5EDE3] rounded-full font-medium">
            手牌 {hand.length}/{ACT1_HAND_SIZE}
          </span>
        </div>
      </div>

      {/* Hand cards */}
      <div className="relative">
        <div className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-white/90 to-transparent z-10 pointer-events-none sm:hidden" />
        <div className="flex items-stretch gap-2 px-2 overflow-x-auto pb-1 sm:justify-center">
          {/* Skip button */}
          <button
            disabled={!isPlayerTurn}
            onClick={onSkip}
            className={`
              relative w-[70px] sm:w-[90px] aspect-[2/3] flex-shrink-0
              bg-gradient-to-br from-[#F5EDE3] via-[#FAF6F1] to-[#EDE8F5]
              border-2 border-[#D4C8B8] rounded-lg shadow-md overflow-hidden
              flex flex-col items-center justify-center gap-1
              transition-all duration-300 ease-out
              ${!isPlayerTurn ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg hover:-translate-y-1 cursor-pointer active:scale-95'}
            `}
          >
            <Eye className="w-5 h-5 text-[#9B8EC4]" />
            <span className="text-[10px] font-bold text-[#6B5B4F]">静观其变</span>
          </button>

          {/* Card list */}
          {hand.map(card => (
            <Act1HandCard
              key={card.id}
              card={card}
              isClickable={isPlayerTurn && !selectedCard}
              isSelected={selectedCard?.id === card.id}
              onClick={() => handleCardClick(card)}
            />
          ))}
        </div>
      </div>
    </div>
  );
});

Act1BottomPanel.displayName = 'Act1BottomPanel';

// ── 单张手牌 ──────────────────────────────────

interface Act1HandCardProps {
  card: Act1Card;
  isClickable: boolean;
  isSelected: boolean;
  onClick: () => void;
}

const EFFECT_COLORS: Record<string, { border: string; bg: string; text: string }> = {
  '说服': { border: 'border-blue-300', bg: 'bg-blue-50', text: 'text-blue-700' },
  '展示': { border: 'border-green-300', bg: 'bg-green-50', text: 'text-green-700' },
  '化解': { border: 'border-amber-300', bg: 'bg-amber-50', text: 'text-amber-700' },
  '挑拨': { border: 'border-red-300', bg: 'bg-red-50', text: 'text-red-700' },
  '转移': { border: 'border-purple-300', bg: 'bg-purple-50', text: 'text-purple-700' },
};

const Act1HandCard: React.FC<Act1HandCardProps> = ({ card, isClickable, isSelected, onClick }) => {
  const style = EFFECT_COLORS[card.type] || { border: 'border-[#D4C8B8]', bg: 'bg-[#F5EDE3]', text: 'text-[#6B5B4F]' };

  return (
    <button
      disabled={!isClickable}
      onClick={onClick}
      className={`
        relative w-[70px] sm:w-[90px] aspect-[2/3] flex-shrink-0
        ${style.bg} border-2 ${isSelected ? 'border-coral-500 ring-2 ring-coral-300' : style.border}
        rounded-lg shadow-md overflow-hidden flex flex-col
        transition-all duration-300 ease-out
        ${!isClickable ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg hover:-translate-y-1 cursor-pointer active:scale-95'}
      `}
    >
      {/* Type badge */}
      <div className="p-1 flex justify-center">
        <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${style.bg} ${style.text} font-medium border ${style.border}`}>
          {card.type}
        </span>
      </div>

      {/* Card name */}
      <div className="flex-1 flex items-center justify-center px-1">
        <span className="text-[11px] sm:text-xs font-bold text-[#2D2D2D] text-center leading-tight">
          {card.name}
        </span>
      </div>

      {/* Hint */}
      <div className="p-1">
        <span className="text-[7px] sm:text-[8px] text-[#9CA3AF] text-center block truncate">
          {card.hint}
        </span>
      </div>
    </button>
  );
};
